import React, { useState, useEffect } from 'react';
import {
  BellIcon,
  CalendarDaysIcon,
  UserIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  EyeIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
// import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';
import adminAPI from '../utils/api';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: 'all',
    recipient_type: 'all',
    is_read: 'all'
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
    count: 0
  });

  // Fetch notifications
  const fetchNotifications = async (page = 1) => {
    try {
      setLoading(true);
      const filtersWithPage = { ...filters, page };
      const data = await adminAPI.getNotifications(filtersWithPage);
      console.log("Admin Notifications API response:", data);
      setNotifications(data.results || data);

      // Update pagination info
      if (data.count !== undefined) {
        // Calculate total pages dynamically from API response
        let totalPages = 1;

        if (data.count > 0) {
          if (data.next || data.previous) {
            // Use current page results length as page size if available
            const currentPageSize = data.results ? data.results.length : 1;
            if (data.next) {
              // Not the last page, use current results length as page size
              totalPages = Math.ceil(data.count / currentPageSize);
            } else {
              // Last page, we are on the final page
              totalPages = page;
            }
          } else {
            // Only one page
            totalPages = 1;
          }
        }

        setPagination({
          currentPage: page,
          totalPages: totalPages,
          hasNext: !!data.next,
          hasPrevious: !!data.previous,
          count: data.count
        });
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des notifications');
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [filters]);

  // Get notification icon
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'booking_created':
      case 'booking_cancelled':
        return <CalendarDaysIcon className="h-5 w-5 text-blue-600" />;
      case 'waitlist_joined':
      case 'waitlist_promoted':
        return <UserIcon className="h-5 w-5 text-green-600" />;
      case 'session_reminder':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />;
      case 'admin_alert':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />;
      default:
        return <BellIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  // Get type badge color
  const getTypeBadgeColor = (type) => {
    switch (type) {
      case 'booking_created':
        return 'bg-blue-100 text-blue-800';
      case 'booking_cancelled':
        return 'bg-red-100 text-red-800';
      case 'waitlist_joined':
        return 'bg-yellow-100 text-yellow-800';
      case 'waitlist_promoted':
        return 'bg-green-100 text-green-800';
      case 'session_reminder':
        return 'bg-purple-100 text-purple-800';
      case 'admin_alert':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get recipient type badge color
  const getRecipientBadgeColor = (type) => {
    switch (type) {
      case 'client':
        return 'bg-blue-100 text-blue-800';
      case 'coach':
        return 'bg-green-100 text-green-800';
      case 'admin':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter options
  const typeOptions = [
    { value: 'all', label: 'Tous les types' },
    { value: 'booking_created', label: 'Réservation créée' },
    { value: 'booking_cancelled', label: 'Réservation annulée' },
    { value: 'waitlist_joined', label: 'Liste d\'attente rejointe' },
    { value: 'waitlist_promoted', label: 'Promotion liste d\'attente' },
    { value: 'session_reminder', label: 'Rappel de séance' },
    { value: 'admin_alert', label: 'Alerte admin' }
  ];

  const recipientTypeOptions = [
    { value: 'all', label: 'Tous les destinataires' },
    { value: 'client', label: 'Clients' },
    { value: 'coach', label: 'Coachs' },
    { value: 'admin', label: 'Admins' }
  ];

  const readStatusOptions = [
    { value: 'all', label: 'Toutes' },
    { value: 'true', label: 'Lues' },
    { value: 'false', label: 'Non lues' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-600">Gérer toutes les notifications du système</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center space-x-4 mb-4">
          <FunnelIcon className="h-5 w-5 text-gray-500" />
          <h3 className="text-lg font-medium text-gray-900">Filtres</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de notification
            </label>
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            >
              {typeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Destinataire
            </label>
            <select
              value={filters.recipient_type}
              onChange={(e) => setFilters({ ...filters, recipient_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            >
              {recipientTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Statut de lecture
            </label>
            <select
              value={filters.is_read}
              onChange={(e) => setFilters({ ...filters, is_read: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            >
              {readStatusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Chargement des notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center">
            <BellIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Aucune notification trouvée</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-6 hover:bg-gray-50 ${
                  !notification.is_read ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    {getNotificationIcon(notification.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className={`text-sm font-medium ${
                        !notification.is_read ? 'text-gray-900' : 'text-gray-700'
                      }`}>
                        {notification.title}
                      </h3>
                      
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        getTypeBadgeColor(notification.type)
                      }`}>
                        {notification.type.replace('_', ' ')}
                      </span>
                      
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        getRecipientBadgeColor(notification.recipient_type)
                      }`}>
                        {notification.recipient_type}
                      </span>
                      
                      {notification.is_read && (
                        <CheckIcon className="h-4 w-4 text-green-500" />
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2">
                      {notification.message}
                    </p>
                    
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>
                        <strong>Destinataire:</strong> {notification.recipient?.full_name || 'N/A'}
                      </span>
                      <span>
                        <strong>Créé:</strong> {formatDate(notification.created_at)}
                      </span>
                      {notification.read_at && (
                        <span>
                          <strong>Lu:</strong> {formatDate(notification.read_at)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => fetchNotifications(pagination.currentPage - 1)}
                disabled={!pagination.hasPrevious}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <button
                onClick={() => fetchNotifications(pagination.currentPage + 1)}
                disabled={!pagination.hasNext}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Page <span className="font-medium">{pagination.currentPage}</span> sur{' '}
                  <span className="font-medium">{pagination.totalPages}</span>
                  {pagination.count && (
                    <span className="ml-2 text-gray-500">
                      ({pagination.count} élément{pagination.count > 1 ? 's' : ''} au total)
                    </span>
                  )}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => fetchNotifications(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrevious}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Précédent</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {(() => {
                    const { currentPage, totalPages } = pagination;
                    const pages = [];
                    const maxVisiblePages = 5;

                    if (totalPages <= maxVisiblePages) {
                      // Show all pages if total is small
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      // Always show first page
                      pages.push(1);

                      if (currentPage > 3) {
                        pages.push('...');
                      }

                      // Show pages around current page
                      const start = Math.max(2, currentPage - 1);
                      const end = Math.min(totalPages - 1, currentPage + 1);

                      for (let i = start; i <= end; i++) {
                        if (i !== 1 && i !== totalPages) {
                          pages.push(i);
                        }
                      }

                      if (currentPage < totalPages - 2) {
                        pages.push('...');
                      }

                      // Always show last page
                      if (totalPages > 1) {
                        pages.push(totalPages);
                      }
                    }

                    return pages.map((page, index) => (
                      page === '...' ? (
                        <span
                          key={`ellipsis-${index}`}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                        >
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => fetchNotifications(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    ));
                  })()}

                  <button
                    onClick={() => fetchNotifications(pagination.currentPage + 1)}
                    disabled={!pagination.hasNext}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Suivant</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
